import updateAvailableQuoteFeedersTable from "../../../modules/feedManager/components/availableQuoteFeeds/utils/updateAvailableQuoteFeedersTable";
import { CicadaAddInError } from "../../errors";
import { DATA_SOURCE_WORKSHEET_NAME } from "../../globalValues";
import { resetTableColors } from "../../resetTableColors";
import { QUOTE_FEEDERS_NAMES_COL, QUOTE_FEEDERS_TABLE_HEADER, QUOTE_FEEDERS_TABLE_NAME } from "./config";
import { CreateQuoteFeedersTableArgs, UpdateQuoteFeedersTableArgs } from "./types";

export const createQuoteFeedersTable = async ({ context, quoteFeeds }: CreateQuoteFeedersTableArgs) => {
  const sheet = context.workbook.worksheets.getItem(DATA_SOURCE_WORKSHEET_NAME);

  let table = sheet.tables.getItemOrNullObject(QUOTE_FEEDERS_TABLE_NAME);
  await context.sync();

  if (!table.isNullObject) {
    await updateAvailableQuoteFeedersTable({ quoteFeeds });
    return;
  }

  table = sheet.tables.add("F1:G1", true /*hasHeaders*/);
  table.name = QUOTE_FEEDERS_TABLE_NAME;
  table.getHeaderRowRange().values = [QUOTE_FEEDERS_TABLE_HEADER];

  table.rows.add(undefined, quoteFeeds);

  resetTableColors({ table });

  table.getRange().format.autofitColumns();
  table.getRange().format.autofitRows();

  await context.sync();
};

export const deleteQuoteFeederTable = async (context: Excel.RequestContext) => {
  const sheet = context.workbook.worksheets.getItem(DATA_SOURCE_WORKSHEET_NAME);
  const table = sheet.tables.getItemOrNullObject(QUOTE_FEEDERS_TABLE_NAME);
  await context.sync();

  if (table.isNullObject) return;

  const range = table.getRange();
  range.delete(Excel.DeleteShiftDirection.up);
};

export const updateQuoteFeedersTable = async ({ context, quoteFeeds }: UpdateQuoteFeedersTableArgs) => {
  await deleteQuoteFeederTable(context);
  await context.sync();
  await createQuoteFeedersTable({ context, quoteFeeds });
  await context.sync();
};

export const getQuoteFeederTableAddress = async (context: Excel.RequestContext) => {
  const sheet = context.workbook.worksheets.getItem(DATA_SOURCE_WORKSHEET_NAME);
  const table = sheet.tables.getItem(QUOTE_FEEDERS_TABLE_NAME);
  const range = table.getRange();
  range.load(["rowCount", "address"]);
  await context.sync();

  const address = `${DATA_SOURCE_WORKSHEET_NAME}!$${QUOTE_FEEDERS_NAMES_COL}$2:$${QUOTE_FEEDERS_NAMES_COL}$${range.rowCount}`;

  return address;
};

export const getQuoteFeederTableData = async (context: Excel.RequestContext) => {
  const sheet = context.workbook.worksheets.getItem(DATA_SOURCE_WORKSHEET_NAME);
  const table = sheet.tables.getItemOrNullObject(QUOTE_FEEDERS_TABLE_NAME);
  await context.sync();

  if (table.isNullObject) {
    throw new CicadaAddInError("Quote Feeder Table Error", "No quote feeder table found");
  }

  const range = table.getRange();
  range.load("values");
  await context.sync();

  return range.values;
};

export const getQuoteFeederIdByName = async (context: Excel.RequestContext, quoteFeederName: string) => {
  const sheet = context.workbook.worksheets.getItem(DATA_SOURCE_WORKSHEET_NAME);
  const table = sheet.tables.getItemOrNullObject(QUOTE_FEEDERS_TABLE_NAME);
  await context.sync();

  if (table.isNullObject) {
    throw new CicadaAddInError("Quote Feeder Table Error", "No quote feeder table found");
  }

  const bodyRange = table.getDataBodyRange();
  bodyRange.load("values");
  await context.sync();

  const values = bodyRange.values;

  const searchIndex = function (arr: string[]) {
    return arr[0] === quoteFeederName;
  };

  const indexMatch = values.findIndex(searchIndex);
  if (indexMatch == -1) {
    console.log(`No quote feeder ID found for ${quoteFeederName}`);
    throw new CicadaAddInError("Quote Feeder Table Error", "No quote feeder found with provided name");
  }

  return values[indexMatch][1];
};
